import { browserOnly } from '@farmersdog/utils';

import track from './track';

/*
  This function is designed to provide a loose interface for tracking click events on the page.
  The `clicked` segment event is a common event that DSI is leveraging to track click behavior
  accross the site; this function is a wrapper around the 'clicked' event to provide some
  guardrails and structure to the event properties. 
  
  This event can also be imported into a local wrapper that extends this interface to add more specific event properties 
  for a page or set of modules on the site.

  If you have questions on what values to pass for a new implementation, please contact DSI.
  
  !! If you want to expand or modify this interface, please work closely with DSI to ensure consistency across the site. !!
*/

export interface TrackClickEventProperties {
  /** The name of the primary module containing the clicked element */
  module_name: string;
  /** The name of the sub-module within the primary module */
  sub_module_name?: string;
  /** The type of click event (e.g., 'button', 'link', 'cta') */
  type?: 'button' | 'select' | 'exit' | 'navigate' | 'contact' | 'expand';
  /** The numerical index of where on the page a parent module/section is located from top to bottom */
  module_index?: number;
  /** Differentiates a sub-level within a parent module/page section */
  module_level?: number;
  /** The visible text content of the clicked element; e.g. the text of a CTA button */
  text?: string;
  /** The URL or route destination of a click action; only used with navigation type clicks */
  destination?: string;
  /** A site identifier; e.g. 'main' */
  site?: 'main' | 'crm' | 'vet-portal' | 'instapage';
  /** The user flow or funnel context of the click; e.g. 'signup' */
  flow?: string;
  /** Used to describe the functionality of a click when there is no text on the element or the text on the element cannot uniquely identify the element. */
  function?: string;
  /** The type of component that was clicked; e.g. 'button', 'link' */
  component?:
    | 'button'
    | 'link'
    | 'accordion'
    | 'tooltip'
    | 'selection'
    | 'carousel'
    | 'text'
    | 'input';
  /** Describes the action performed by the click; e.g. 'expand_answer', 'navigate_to_section' */
  action?: string;
  /** The pet name of the clicked element; to be used when a pet-specific context is needed */
  pet_name?: string;
}

const eventName = 'clicked';

export function trackClickEvent(properties: TrackClickEventProperties) {
  // Explicitly set page.path and page.url from window.location to override Segment's default
  // behavior of using the canonical URL (which may differ from the actual page path)

  const page = browserOnly(
    window => ({
      path: window.location.pathname,
      url: window.location.href,
    }),
    () => undefined
  );

  // Only include page context if it's available (browser environment)
  const context = page ? { page } : {};

  track(eventName, properties as unknown as Record<string, unknown>, {
    context,
  });
}

export default trackClickEvent;
